<template>
	<div class="initial-chart-section">
		<AppCard no-header>
			<template #body>
				<AppFilterGroup v-if="filters && filters.length" :filters="filters" @changed-filter="onFilter" class="card-filters"/>
				<div class="row initial-chart-container">
					<div class="col-9 initial-chart-col">
						<AppChart
							type="area"
							:series="invoicesSeries"
							:xaxis="xaxisValues"
							:xaxis-annotations="xaxisAnnotations"
							:gradient-colors="chartColors"
							:colors="['#6c33cb']"
							height="100%"
							full-width
							class="initial-chart"
							:formatter="r => currencyStore.readableCurrencyValue(r, false, true, true)"
							:dark="userStore.darkMode"
						/>
					</div>
					<div class="col-3 initial-side-info">
						<UserInitialCardInfo class="initial-side-info-square" blue :title="getString('total_vat')"
											 :value="stats.totalWithVat"/>
						<UserInitialCardInfo class="initial-side-info-square" blue :title="getString('total_without_vat')"
											 :value="stats.totalWithoutVat"/>
						<UserInitialCardInfo class="initial-side-info-square danger" blue :title="getString('failed_total')"
											 :value="stats.failedTotal"/>
						<UserInitialCardInfo class="initial-side-info-square danger clickable" blue no-currency
											 :title="getString('failed_count')"
											 :value="stats.failedCount" @click="clickFailed"/>
					</div>
				</div>
			</template>
		</AppCard>
	</div>
</template>

<script>
import AppCard from "@/shared/components/AppCard.vue";
import AppChart from "@/shared/components/AppChart.vue";
import AppFilterGroup from "@/shared/components/AppFilterGroup.vue";
import UserInitialCardInfo from "@/user/components/analytics/UserInitialCardInfo.vue";
import {useCurrencyStore, useLanguageStore} from "@/shared/stores";
import {useUserStore} from "@/user/stores";

export default {
	name: "InvoicesStatsCard",
	emits: ["filter-failed", "date-filter-changed", "filters-changed"],
	components: {AppCard, AppChart, AppFilterGroup, UserInitialCardInfo},
	props: {
		invoices: {type: Array, default: () => []}
	},
	setup() {
		const currencyStore = useCurrencyStore();
		const languageStore = useLanguageStore();
		return {currencyStore, getString: languageStore.getString, languageStore, userStore: useUserStore()};
	},
	data() {
		let end = new Date();
		let start = new Date();
		start.setMonth(start.getMonth() - 1);
		return {
			timeSpans: [],
			filters: [{
				type: 'date',
				key: 'date',
				tableName: 'businesses_invoices',
				text: this.getString('date'),
				active: true,
				finalValue: '',
				value: {start, end}
			}, {
				type: "checklist",
				key: "type",
				text: this.getString('type'),
				values: ['commissions_stripe', 'commissions_pos', 'commissions_other', 'commissions_payout', 'shop', 'super_invoice', 'subscription', 'dispute_management_fee', 'recharge_credit'].map(t => this.getString(t)),
				keysArray: ['commissions_stripe', 'commissions_pos', 'commissions_other', 'commissions_payout', 'shop', 'super_invoice', 'subscription', 'dispute_management_fee', 'recharge_credit'],
				active: false,
				selectedIndexes: [],
			}]
		};
	},
	computed: {
		chartColors() {
			return this.userStore.darkMode ? [null, '#0D71DE'] : [null, '#6c33cb'];
		},
		xaxisValues() {
			return this.timeSpans.map((t, i) => this.localeString(t, i));
		},
		invoicesSeries() {
			return [{name: 'invoices', data: this.dividedSeries}]
		},
		filteredInvoices() {
			// No client-side filtering needed - invoices are already filtered by the API
			return this.invoices || [];
		},
		stats() {
			let res = {totalWithVat: 0, totalWithoutVat: 0, failedTotal: 0, failedCount: 0};
			this.filteredInvoices.forEach(inv => {
				let amount = this.currencyStore.dbToActualValue(inv.amount);
				res.totalWithVat += amount;
				res.totalWithoutVat += amount / 1.22;
				if (inv.state === 'failed' || inv.state === 'refused') {
					res.failedTotal += amount;
					res.failedCount++;
				}
			});
			return res;
		},
		dividedSeries() {
			let arr = this.timeSpans.map(() => 0);
			if (!this.filteredInvoices || !this.filteredInvoices.length) {
				return arr;
			}
			let start = this.timeSpans[0].getTime();
			this.filteredInvoices.forEach(inv => {
				if (!inv.date) {
					return;
				}
				let d = inv.date;
				if (typeof d === 'string') {
					d = new Date(inv.date)
				}
				let idx = Math.floor((d.getTime() - start) / (24 * 60 * 60 * 1000));
				if (idx >= 0 && idx < arr.length) {
					arr[idx] += this.currencyStore.dbToActualValue(inv.amount);
				}
			});
			for (let i = 1; i < arr.length; i++) {
				arr[i] += arr[i - 1];
			}
			return arr;
		},
		xaxisAnnotations() {
			return [];
		}
	},
	methods: {
		localeString(date, index = 0) {
			date = date ? new Date(date) : new Date();
			const lang = this.languageStore.lang || 'en-US';
			if (this.timeSpans && this.timeSpans.length <= 7) {
				// For 7 days or less, show weekday names
				return date.toLocaleDateString([lang], {weekday: 'short'});
			}
			if (this.timeSpans && this.timeSpans.length <= 31) {
				// For up to a month, show day number only to save space
				return date.getDate().toString();
			}
			if (this.timeSpans && this.timeSpans.length <= 90) {
				// For up to 3 months, show day and month
				return date.toLocaleDateString([lang], {day: 'numeric', month: 'short'});
			}
			if (this.timeSpans && this.timeSpans.length <= 366) {
				// For up to a year, show month names
				return date.toLocaleDateString([lang], {month: 'short'});
			}
			return date.getFullYear().toString();
		},
		getDatesEveryDayBetween(start, end) {
			let dates = [];
			let current = new Date(start);
			current.setHours(0, 0, 0, 0);
			let last = new Date(end);
			last.setHours(0, 0, 0, 0);
			while (current <= last) {
				dates.push(new Date(current));
				current.setDate(current.getDate() + 1);
			}
			return dates;
		},
		onFilter() {
			let dateFilter = this.filters.find(f => f.key === 'date');
			let typeFilter = this.filters.find(f => f.key === 'type');
			
			if (dateFilter) {
				this.timeSpans = this.getDatesEveryDayBetween(dateFilter.value.start, dateFilter.value.end);
			}
			
			// Emit filter changes to parent
			this.$emit('filters-changed', {
				date: dateFilter ? {
					start: dateFilter.value.start,
					end: dateFilter.value.end
				} : null,
				type: typeFilter && typeFilter.active ? typeFilter.keysArray.filter((v, i) => typeFilter.selectedIndexes.includes(i)) : null
			});
		},
		clickFailed() {
			this.$emit('filter-failed');
		}
	},
	created() {
		let dateFilter = this.filters.find(f => f.key === 'date');
		if (dateFilter) {
			this.timeSpans = this.getDatesEveryDayBetween(dateFilter.value.start, dateFilter.value.end);
		}
	},
};
</script>

<style lang="scss" scoped>
.initial-chart-section {
	max-width: 1400px;
	width: 100%;
	margin: 0 auto;

	> .card-container {
		min-width: 100%;

		.initial-chart-container {
			align-items: stretch;

			.initial-side-info {
				padding-right: 12px;
			}
		}

		.chart-container {
			&:deep(.card-container) {
				box-shadow: none;

				.card-body {
					padding-top: 0;
					padding-bottom: 0;
				}
			}

			&:deep(.chart-svg-container) {
				height: 300px;
				padding-left: 10px;
			}
		}

		&:deep(.card-body) {
			padding: 0;

			.initial-chart-col {
				justify-content: center;
				width: 75%;
				max-width: calc(100% - 250px);
				min-width: calc(100% - 300px);
			}

			.initial-side-info {
				min-width: 250px;
				max-width: 300px;
				padding-bottom: 12px;
				padding-top: 12px;

				.initial-side-info-square {
					&.danger {
						background-color: rgba(252, 34, 145, 0.10);
						transition: background-color 0.2s ease-in-out;

						&.clickable {
							box-shadow: inset 0 0 0 1px var(--color-danger);
							cursor: pointer;

							&:hover {
								background-color: rgba(252, 34, 145, 0.15);
							}
						}
					}

					&:not(:last-child) {
						margin-bottom: 12px;
					}

					.info-last-period-value {
						display: none;
					}
				}
			}

			overflow: hidden;
			border-radius: 13px;

			.card-filters {
				position: relative;
				background-color: var(--color-bg-primary-2);
				padding: 10px 12px;

				.filter-wrapper.active-filter {
					.filter {
						border: 1px solid var(--color-border-alpha);
					}
				}
			}

			@media (max-width: 1080px) {
				.chart-container {
					padding-top: 8px;
				}
				.initial-chart-col {
					width: 100%;
					max-width: 100%;
					min-width: 100%;
				}
				.initial-side-info {
					flex-direction: row;
					justify-content: space-between;
					align-items: flex-start;
					min-width: 100%;
					max-width: 100%;
					padding-bottom: 0;

					.initial-side-info-square {
						height: unset;
						margin-left: 12px;
						margin-bottom: 12px;
					}
				}
			}

			@media (max-width: 960px) {
				.initial-side-info {
					flex-wrap: wrap;
					padding-top: 0;

					.initial-side-info-square {
						width: calc(50% - 12px);
						margin-left: 12px;
						margin-bottom: 12px;
					}
				}
			}

			@media (max-width: 768px) {
				.initial-side-info {

					.initial-side-info-square {
						width: calc(50% - 8px);
						margin-left: 8px;
						margin-bottom: 8px;

						&:not(:last-child) {
							margin-bottom: 8px;
						}
					}
				}
			}

			@media (max-width: 460px) {
				.initial-side-info {
					justify-content: center;

					.initial-side-info-square {
						width: calc(100% - 8px);
					}
				}
			}

		}
	}
}
</style>
